import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, { useEffect, useMemo, useRef, useState } from 'react';

const defaultFormatCurrency = (amount = 0) =>
  Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);

const formatReadableDate = (value) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// A4 dimensions in pixels (at 96 DPI)
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MM_TO_PX = 3.779527559; // 1mm = 3.779527559px at 96 DPI
const A4_WIDTH_PX = A4_WIDTH_MM * MM_TO_PX; // ~794px
const A4_HEIGHT_PX = A4_HEIGHT_MM * MM_TO_PX; // ~1123px
const PAGE_MARGIN = 5; // 5mm margin
const CONTENT_HEIGHT_PX = A4_HEIGHT_PX - (PAGE_MARGIN * 2 * MM_TO_PX);

const InvoicePrint = ({ invoice, isOpen, onClose, formatCurrency }) => {
  const scrollRef = useRef(null);
  const printableRefs = useRef([]);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [estimatedPages, setEstimatedPages] = useState(1);
  const [pageContent, setPageContent] = useState([]);
  const formatMoney = formatCurrency || defaultFormatCurrency;
  const lineItems = invoice?.lineItems || [];
  const summary = invoice?.summary || {};
  const taxes = summary.taxes || [];
  const discount = invoice?.discount || {};
  const isAmenityBooking = invoice?.isAmenityBooking || false; // Check if this is an amenity booking invoice

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Initialize pageContent when component opens
  useEffect(() => {
    if (!isOpen) {
      setPageContent([]);
      printableRefs.current = [];
    }
  }, [isOpen]);

  const formatStatusLabel = (v) => {
    if (!v) return '—';
    const s = String(v).toLowerCase();
    if (s === 'draft') return 'Draft';
    if (s === 'sent') return 'Sent';
    if (s === 'paid') return 'Paid';
    if (s === 'overdue') return 'Overdue';
    if (s === 'cancelled') return 'Cancelled';
    return v;
  };
  const formatModeLabel = (v) => {
    if (!v) return '—';
    const s = String(v).toLowerCase();
    if (s === 'bank-transfer') return 'Bank Transfer';
    if (s === 'upi') return 'UPI';
    if (s === 'cheque') return 'Cheque';
    if (s === 'cash') return 'Cash';
    return v;
  };

  const meta = useMemo(() => {
    if (!invoice) {
      return {};
    }

    const { formData = {}, labels = {} } = invoice;
    const readableInvoiceDate = formatReadableDate(formData.date);
    const readablePoDate = formatReadableDate(formData.poDate);
    const readableGeneratedDate = formatReadableDate(invoice.generatedAt);

    return {
      invoiceNumber: invoice.invoiceNumber || 'Draft',
      generatedOn: readableGeneratedDate,
      orderDate: readableInvoiceDate,
      poNumber: formData.poNumber || '—',
      poDate: readablePoDate,
      paymentMode: formData.paymentMode || '—',
      paymentTerms: formData.paymentTerms || '—',
      site: labels.site || '—',
      billingAddress: labels.billingAddress || '—',
      shippingAddress: labels.shippingAddress || labels.billingAddress || '—',
      client: formData.clientSelection || '—',
      transportationMode: formData.transportationMode || '—',
      transportationTerms: formData.transportationTerms || '—',
      shippingPackages: formData.shippingPackages || '—',
      grossQuantity: formData.grossQuantity || '—',
      netQuantity: formData.netQuantity || '—',
      pocName: formData.pocName || '—',
      pocRole: formData.pocRole || '—',
      pocPhone: formData.pocPhone || '—',
      pocEmail: formData.pocEmail || '—',
      specialNotes: formData.specialNotes || '—',
      termsOfSale: formData.termsOfSale || '—',
      bookingNotes: formData.bookingNotes || '—', // Notes from amenity booking
      estimatedShipDate: formatReadableDate(formData.estimatedShipDate),
      invoiceExpiry: formatReadableDate(formData.dateOfExpiry),
    };
  }, [invoice]);

  // Calculate page content distribution
  useEffect(() => {
    if (isOpen && invoice && lineItems.length >= 0) {
      // Calculate how many line items fit on first page
      // Estimate: header (~200px) + client info (~300px) + transportation/POC (~200px) + table header (~50px) + totals (~400px) + taxes (~200px) + notes (~150px) + footer (~40px)
      const firstPageFixedHeight = 200 + 300 + 200 + 50 + 400 + 200 + 150 + 40;
      const availableHeightFirstPage = CONTENT_HEIGHT_PX - firstPageFixedHeight;
      const availableHeightOtherPages = CONTENT_HEIGHT_PX - 40; // Just footer on other pages
      
      // Estimate line item row height (~45px per row)
      const lineItemRowHeight = 45;
      const itemsPerFirstPage = Math.max(0, Math.floor(availableHeightFirstPage / lineItemRowHeight));
      const itemsPerOtherPage = Math.max(0, Math.floor(availableHeightOtherPages / lineItemRowHeight));
      
      const totalItems = lineItems.length;
      const pages = [];
      
      // If no items or all items fit on first page
      if (totalItems === 0 || totalItems <= itemsPerFirstPage) {
        // All content fits on first page
        pages.push({
          pageNumber: 1,
          lineItems: lineItems,
          showHeader: true,
          showClientInfo: true,
          showTransportation: true,
          showTotals: true,
          showTaxes: true,
          showNotes: true,
        });
        setEstimatedPages(1);
      } else {
        // First page with header and some items
        const firstPageItems = lineItems.slice(0, itemsPerFirstPage);
        pages.push({
          pageNumber: 1,
          lineItems: firstPageItems,
          showHeader: true,
          showClientInfo: true,
          showTransportation: true,
          showTotals: false,
          showTaxes: false,
          showNotes: false,
        });
        
        // Remaining items
        let remainingItems = lineItems.slice(itemsPerFirstPage);
        let pageNum = 2;
        
        while (remainingItems.length > 0) {
          const pageItems = remainingItems.slice(0, itemsPerOtherPage);
          remainingItems = remainingItems.slice(itemsPerOtherPage);
          
          pages.push({
            pageNumber: pageNum,
            lineItems: pageItems,
            showHeader: false,
            showClientInfo: false,
            showTransportation: false,
            showTotals: remainingItems.length === 0, // Show totals on last page
            showTaxes: remainingItems.length === 0, // Show taxes on last page
            showNotes: remainingItems.length === 0, // Show notes on last page
            isContinuation: true,
          });
          
          pageNum++;
        }
        
        setEstimatedPages(pages.length);
      }
      
      setPageContent(pages);
    }
  }, [isOpen, invoice, lineItems.length]);

  if (!isOpen || !invoice) {
    return null;
  }

  const handleDownloadPdf = async () => {
    if (!printableRefs.current || printableRefs.current.length === 0 || isGeneratingPdf) {
      return;
    }
    try {
      setIsGeneratingPdf(true);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 5;
      const availableWidth = pageWidth - margin * 2;
      const availableHeight = pageHeight - margin * 2;

      // Process each page
      for (let i = 0; i < printableRefs.current.length; i++) {
        const pageRef = printableRefs.current[i];
        if (!pageRef) continue;

        if (i > 0) {
          pdf.addPage();
        }

        const canvas = await html2canvas(pageRef, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
        });

        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = availableWidth / imgWidth;
        const scaledWidth = imgWidth * ratio;
        const scaledHeight = imgHeight * ratio;

        const offsetX = (pageWidth - scaledWidth) / 2;
        const offsetY = margin;

        pdf.addImage(
          canvas.toDataURL('image/png', 1.0),
          'PNG',
          offsetX,
          offsetY,
          scaledWidth,
          scaledHeight,
          undefined,
          'FAST'
        );
      }

      pdf.save(`${invoice.invoiceNumber || 'invoice'}.pdf`);
    } catch (error) {
      console.error('Failed to generate invoice PDF', error);
      alert('Unable to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-800">
      {/* Print Styles */}
      <style>{`
        @media print {
          .pdf-viewer-toolbar,
          .pdf-viewer-controls {
            display: none !important;
          }
          .pdf-page {
            page-break-after: always;
            margin: 0 !important;
            box-shadow: none !important;
            break-after: page;
          }
          .pdf-viewer-container {
            background: white !important;
            padding: 0 !important;
          }
        }
      `}</style>

      {/* PDF Viewer Toolbar */}
      <div className="pdf-viewer-toolbar flex-shrink-0 bg-gray-900 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-base font-semibold" style={{ color: '#fff' }}>
              Invoice Preview - PDF Viewer
            </h2>
            <div className="h-5 w-px bg-gray-600"></div>
            <div className="flex items-center gap-2 text-sm text-gray-300">
              <span className="font-medium">Estimated Pages:</span>
              <span className="px-2 py-1 bg-gray-700 rounded text-white font-semibold">
                {estimatedPages} {estimatedPages === 1 ? 'page' : 'pages'}
              </span>
            </div>
            <div className="h-5 w-px bg-gray-600"></div>
            <div className="text-xs text-gray-400">
              A4 Size (210mm × 297mm)
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={isGeneratingPdf}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isGeneratingPdf ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Generating...
                </>
              ) : (
                <>
                  <span>📥</span>
                  Download PDF
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-gray-700 hover:bg-gray-600 rounded transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* PDF Viewer Container */}
      <div className="pdf-viewer-container flex-1 overflow-auto bg-gray-600 p-8" ref={scrollRef}>
        <div className="flex flex-col items-center gap-6 min-h-full py-8">
          {pageContent.length > 0 ? (
            pageContent.map((page, pageIndex) => {
              // Calculate starting line number for continuation pages
              const startLineNumber = pageIndex === 0 
                ? 1 
                : pageContent.slice(0, pageIndex).reduce((sum, p) => sum + p.lineItems.length, 0) + 1;

              return (
                <div
                  key={page.pageNumber}
                  ref={(el) => {
                    if (el) printableRefs.current[pageIndex] = el;
                  }}
                  className="pdf-page bg-white shadow-2xl"
                  style={{
                    width: `${A4_WIDTH_PX}px`,
                    minHeight: `${A4_HEIGHT_PX}px`,
                    padding: `${PAGE_MARGIN * MM_TO_PX}px`,
                    boxSizing: 'border-box',
                  }}
                >
                  <div className="flex flex-col gap-3" style={{ minHeight: '100%' }}>
                    {/* Header - only on first page */}
                    {page.showHeader && (
                      <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 md:flex-row md:items-center md:justify-between">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">
                            Innoworx · Invoice
                          </p>
                          <h2 className="text-2xl font-semibold text-gray-900">
                            {meta.invoiceNumber}
                          </h2>
                          <p className="text-xs text-gray-500">
                            Generated on {meta.generatedOn || formatReadableDate(new Date())}
                          </p>
                        </div>
                        {page.showTotals && (
                          <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-right">
                            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">
                              Grand Total
                            </p>
                            <p className="text-2xl font-bold text-emerald-700">
                              {formatMoney(summary.grandTotal || 0)}
                            </p>
                            <p className="text-xs text-gray-500">Payable immediately</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Continuation header for subsequent pages */}
                    {page.isContinuation && (
                      <div className="flex flex-col gap-2 border-b border-gray-200 pb-2">
                        <h2 className="text-lg font-semibold text-gray-700">Invoice (Continued)</h2>
                        <p className="text-xs text-gray-600">{meta.invoiceNumber}</p>
                      </div>
                    )}

                    {/* Client Info - only on first page */}
                    {page.showClientInfo && (
                      <div className="grid gap-3 md:grid-cols-2">
                        <div className="rounded-xl border border-gray-100 p-3">
                          <p className="text-xs font-semibold text-gray-500">Client</p>
                          <p className="mt-1 text-base font-semibold text-gray-900">{meta.client}</p>
                          <p className="text-xs text-gray-600">Site: {meta.site}</p>
                          <div className="mt-3 text-xs text-gray-600">
                            <p className="font-semibold text-gray-800">Billing Address</p>
                            <p>{meta.billingAddress}</p>
                          </div>
                          <div className="mt-3 text-xs text-gray-600">
                            <p className="font-semibold text-gray-800">Shipping Address</p>
                            <p>{meta.shippingAddress}</p>
                          </div>
                        </div>
                        <div className="rounded-xl border border-gray-100 p-3">
                          <dl className="space-y-1.5 text-xs text-gray-600">
                            <div className="flex items-center justify-between">
                              <dt className="font-semibold text-gray-800">Invoice Date</dt>
                              <dd>{meta.orderDate || '—'}</dd>
                            </div>
                            {!isAmenityBooking && (
                              <>
                                <div className="flex items-center justify-between">
                                  <dt className="font-semibold text-gray-800">PO Number</dt>
                                  <dd>{meta.poNumber}</dd>
                                </div>
                                <div className="flex items-center justify-between">
                                  <dt className="font-semibold text-gray-800">PO Date</dt>
                                  <dd>{meta.poDate || '—'}</dd>
                                </div>
                              </>
                            )}
                            <div className="flex items-center justify-between">
                              <dt className="font-semibold text-gray-800">Payment Mode</dt>
                              <dd>{meta.paymentMode}</dd>
                            </div>
                            {!isAmenityBooking && (
                              <div className="flex items-center justify-between">
                                <dt className="font-semibold text-gray-800">Payment Terms</dt>
                                <dd className="text-right">{meta.paymentTerms}</dd>
                              </div>
                            )}
                            <div className="flex items-center justify-between">
                              <dt className="font-semibold text-gray-800">Est. Ship Date</dt>
                              <dd>{meta.estimatedShipDate || '—'}</dd>
                            </div>
                            <div className="flex items-center justify-between">
                              <dt className="font-semibold text-gray-800">Invoice Expiry</dt>
                              <dd>{meta.invoiceExpiry || '—'}</dd>
                            </div>
                          </dl>
                        </div>
                      </div>
                    )}

                    {/* Transportation & POC - only on first page */}
                    {page.showTransportation && (
                      <div className="grid gap-3 md:grid-cols-2">
                        {!isAmenityBooking && (
                          <div className="rounded-xl border border-gray-100 p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Transportation
                            </p>
                            <dl className="mt-2 space-y-1.5 text-xs text-gray-600">
                              <div className="flex items-center justify-between">
                                <dt>Mode</dt>
                                <dd>{meta.transportationMode}</dd>
                              </div>
                              <div className="flex items-center justify-between">
                                <dt>Terms</dt>
                                <dd>{meta.transportationTerms}</dd>
                              </div>
                              <div className="flex items-center justify-between">
                                <dt>Packages</dt>
                                <dd>{meta.shippingPackages}</dd>
                              </div>
                              <div className="flex items-center justify-between">
                                <dt>Gross Qty</dt>
                                <dd>{meta.grossQuantity}</dd>
                              </div>
                              <div className="flex items-center justify-between">
                                <dt>Net Qty</dt>
                                <dd>{meta.netQuantity}</dd>
                              </div>
                            </dl>
                          </div>
                        )}
                        <div className={`rounded-xl border border-gray-100 p-3 ${isAmenityBooking ? 'md:col-span-2' : ''}`}>
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                            Point of Contact
                          </p>
                          <div className="grid grid-cols-4 gap-3 text-xs text-gray-600">
                            <div>
                              <dt className="font-semibold text-gray-700 mb-1">Name</dt>
                              <dd>{meta.pocName}</dd>
                            </div>
                            <div>
                              <dt className="font-semibold text-gray-700 mb-1">Role</dt>
                              <dd>{meta.pocRole}</dd>
                            </div>
                            <div>
                              <dt className="font-semibold text-gray-700 mb-1">Phone</dt>
                              <dd>{meta.pocPhone}</dd>
                            </div>
                            <div>
                              <dt className="font-semibold text-gray-700 mb-1">Email</dt>
                              <dd>{meta.pocEmail}</dd>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Line Items Table */}
                    <div className="overflow-hidden rounded-xl border border-gray-200">
                      <div className={`grid grid-cols-12 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600`}>
                        <span className="col-span-1">#</span>
                        <span className={isAmenityBooking ? "col-span-5" : "col-span-4"}>Item / Service</span>
                        {!isAmenityBooking && <span className="col-span-2 text-center">Qty</span>}
                        <span className={isAmenityBooking ? "col-span-3 text-center" : "col-span-2 text-center"}>Units</span>
                        <span className={isAmenityBooking ? "col-span-1 text-center" : "col-span-1 text-center"}>Rate</span>
                        <span className={isAmenityBooking ? "col-span-2 text-right" : "col-span-2 text-right"}>Amount</span>
                      </div>
                      {page.lineItems.length ? (
                        page.lineItems.map((item, itemIndex) => {
                          const lineNumber = startLineNumber + itemIndex;
                          return (
                            <div
                              key={`invoice-row-${page.pageNumber}-${itemIndex}`}
                              className="grid grid-cols-12 border-t border-gray-100 px-3 py-2 text-xs text-gray-700"
                            >
                              <span className="col-span-1 font-semibold text-gray-500">
                                {item.lineNumber || lineNumber}
                              </span>
                              <span className={isAmenityBooking ? "col-span-5 font-semibold text-gray-900" : "col-span-4 font-semibold text-gray-900"}>
                                {item.name || `Line item ${lineNumber}`}
                              </span>
                              {!isAmenityBooking && (
                                <span className="col-span-2 text-center">
                                  {item.quantity || '-'}
                                </span>
                              )}
                              <span className={isAmenityBooking ? "col-span-3 text-center" : "col-span-2 text-center"}>{item.units || '-'}</span>
                              <span className={isAmenityBooking ? "col-span-1 text-center" : "col-span-1 text-center"}>
                                {item.rate ? formatMoney(parseFloat(item.rate)) : '-'}
                              </span>
                              <span className={isAmenityBooking ? "col-span-2 text-right font-semibold text-gray-900" : "col-span-2 text-right font-semibold text-gray-900"}>
                                {item.amount ? formatMoney(parseFloat(item.amount)) : formatMoney(0)}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <div className="px-3 py-4 text-center text-xs text-gray-500">
                          No line items added yet.
                        </div>
                      )}
                    </div>

                    {/* Discount & Totals - only on last page */}
                    {page.showTotals && (
                      <>
                        <div className="grid gap-4 lg:grid-cols-2">
                          <div className="rounded-xl border border-gray-100 p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Discount & Charges
                            </p>
                            <div className="mt-3 space-y-2 text-xs text-gray-600">
                              <div className="flex items-center justify-between">
                                <span>Type</span>
                                <span className="font-semibold text-gray-900">
                                  {discount.type || '—'}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Value</span>
                                <span className="font-semibold text-gray-900">
                                  {discount.value || discount.value === 0
                                    ? discount.value
                                    : '—'}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Shipping / Handling</span>
                                <span className="font-semibold text-gray-900">
                                  {discount.shippingHandling
                                    ? formatMoney(parseFloat(discount.shippingHandling))
                                    : formatMoney(0)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Insurance</span>
                                <span className="font-semibold text-gray-900">
                                  {discount.insurance
                                    ? formatMoney(parseFloat(discount.insurance))
                                    : formatMoney(0)}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="rounded-xl border border-gray-100 p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Totals
                            </p>
                            <div className="mt-3 space-y-2 text-xs text-gray-700">
                              <div className="flex items-center justify-between">
                                <span>Subtotal</span>
                                <span className="font-semibold">
                                  {formatMoney(summary.subtotal || 0)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Discount</span>
                                <span className="font-semibold text-emerald-600">
                                  - {formatMoney(summary.computedDiscount || 0)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Subtotal After Discount</span>
                                <span className="font-semibold">
                                  {formatMoney(summary.subtotalAfterDiscount || 0)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Shipping / Handling</span>
                                <span className="font-semibold">
                                  {formatMoney(summary.shippingHandling || 0)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Insurance</span>
                                <span className="font-semibold">
                                  {formatMoney(summary.insurance || 0)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Net Total (Before Tax)</span>
                                <span className="font-semibold">
                                  {formatMoney(summary.netTotalBeforeTax || 0)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Taxes - only on last page */}
                        {page.showTaxes && (
                          <div className="rounded-xl border border-gray-100 p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Taxes
                            </p>
                            <div className="mt-3 space-y-2 text-xs text-gray-700">
                              {taxes.length ? (
                                taxes.map((tax) => (
                                  <div
                                    key={tax.id}
                                    className="flex items-center justify-between border-b border-dashed border-gray-200 pb-2 last:border-b-0 last:pb-0"
                                  >
                                    <span>
                                      {tax.label || 'Tax'} ({tax.percentage || 0}% on{' '}
                                      {tax.applyOn || 'subtotal'})
                                    </span>
                                    <span className="font-semibold">
                                      {formatMoney(tax.amount || 0)}
                                    </span>
                                  </div>
                                ))
                              ) : (
                                <p className="text-gray-500">No taxes applied.</p>
                              )}
                              <div className="flex items-center justify-between border-t border-gray-100 pt-2 text-sm font-bold text-gray-900">
                                <span>Total Tax</span>
                                <span>{formatMoney(summary.totalTax || 0)}</span>
                              </div>
                              <div className="flex items-center justify-between text-lg font-bold text-emerald-700">
                                <span>Grand Total</span>
                                <span>{formatMoney(summary.grandTotal || 0)}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Notes - only on last page */}
                        {page.showNotes && (
                          <div className={isAmenityBooking ? "grid gap-3 md:grid-cols-1" : "grid gap-3 md:grid-cols-2"}>
                            {isAmenityBooking ? (
                              <div className="rounded-xl border border-gray-100 p-3">
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                  Notes
                                </p>
                                <p className="mt-2 text-xs text-gray-600 whitespace-pre-line">
                                  {meta.bookingNotes !== '—' ? meta.bookingNotes : '—'}
                                </p>
                              </div>
                            ) : (
                              <>
                                <div className="rounded-xl border border-gray-100 p-3">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Special Notes
                                  </p>
                                  <p className="mt-2 text-xs text-gray-600 whitespace-pre-line">
                                    {meta.specialNotes}
                                  </p>
                                </div>
                                <div className="rounded-xl border border-gray-100 p-3">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Terms of Sale
                                  </p>
                                  <p className="mt-2 text-xs text-gray-600 whitespace-pre-line">
                                    {meta.termsOfSale}
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* Page Footer */}
                    <div className="mt-auto pt-2 border-t border-gray-200 text-center text-xs text-gray-500">
                      Page {page.pageNumber} of {estimatedPages}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="pdf-page bg-white shadow-2xl p-8" style={{ width: `${A4_WIDTH_PX}px`, minHeight: `${A4_HEIGHT_PX}px` }}>
              <div className="text-center text-gray-500">Loading preview...</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoicePrint;

